import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useLoader, useFrame } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { Link } from 'react-router-dom';
import './HeroSection.css';
// Import the 3D model
import modelPath from '../../asset/3D/model.glb';
import danceModelPath from '../../asset/3D/dance.glb';
import fallModelPath from '../../asset/3D/fall.glb';
import standModelPath from '../../asset/3D/standup.glb';
// Import textures
import normalMapPath from '../../asset/3D/texture/normal.png';
// import eyesTexturePath from '../../asset/Viola/banner.jpg';
import eyesTexturePath from '../../asset/3D/texture/eyes.png';


// Model component to load and display the 3D model
const Model = ({ textureOffset = { x: 0, y: 0.66 }, textureTiling = { x: 1, y: 1 }, flipY = false }) => {
  const gltf = useLoader(GLTFLoader, modelPath);
  const danceGltf = useLoader(GLTFLoader, danceModelPath);
  const fallGltf = useLoader(GLTFLoader, fallModelPath);
  const standGltf = useLoader(GLTFLoader, standModelPath);
  // const normalMap = useLoader(THREE.TextureLoader, normalMapPath);
  const eyesTexture = useLoader(THREE.TextureLoader, eyesTexturePath);
  
  // Animation mixer reference and state
  const mixerRef = useRef(null);
  const danceActionRef = useRef(null);
  const fallActionRef = useRef(null);
  const standActionRef = useRef(null);
  const modelRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const initialStateRef = useRef(null); // Store initial model state
  
  // Set up normal map correctly
  useEffect(() => {
    // Make sure textureOffset and textureTiling are defined before using them
    const offset = textureOffset || { x: 0, y: 0.66 };
    const tiling = textureTiling || { x: 10, y: 10 };
    
    // Configure eyes texture with tiling and offset
    eyesTexture.wrapS = THREE.RepeatWrapping;
    eyesTexture.wrapT = THREE.RepeatWrapping;
    
    // Set repeat with Y flipping if needed
    eyesTexture.repeat.set(
      tiling.x, 
      flipY ? -tiling.y : tiling.y
    );
    
    eyesTexture.offset.set(
      offset.x, 
      flipY ? 1 + offset.y : offset.y // When flipped, adjust offset to maintain position
    );
    
    // Create materials
    const pinkMaterial = new THREE.MeshStandardMaterial({ 
      color: '#E32961',
      roughness: 0.7,
      metalness: 0.2,
      // normalMap: normalMap,
      normalScale: new THREE.Vector2(1, 1)
    });
    
    const whiteMaterial = new THREE.MeshStandardMaterial({ 
      color: '#FFFFFF',
      roughness: 0.3,  // Lower roughness for a shinier look
      metalness: 0.1,
      emissive: '#333333',  // Slight emissive property to make it brighter
      emissiveIntensity: 0.2,
      // normalMap: normalMap,
      normalScale: new THREE.Vector2(0.8, 0.8)
    });
    
    // Create screen material with eyes texture
    const screenMaterial = new THREE.MeshStandardMaterial({
      map: eyesTexture,
      emissive: '#FFFFFF',
      emissiveMap: eyesTexture,
      emissiveIntensity: 0.5,
      roughness: 0.2,
      metalness: 0.3
    });
    
    // Create black material for the screen
    const blackMaterial = new THREE.MeshStandardMaterial({
      color: '#000000',
      roughness: 0.5,
      metalness: 0.3,
    });
    
    // Traverse the scene to find all meshes
    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        console.log('Mesh found:', child.name, child);
        
        // Check if mesh has UV coordinates
        if (!child.geometry.attributes.uv) {
          console.warn(`Mesh ${child.name} has no UV coordinates for normal mapping`);
        }
        
        // Apply specific materials based on mesh name
        if (child.name === 'head_eyes_low' || child.name.includes('head_eyes')) {
          // Apply eyes texture to the eyes mesh
          child.material = screenMaterial;
          
          // Ensure the texture is properly mapped
          if (child.geometry.attributes.uv) {
            console.log(`${child.name} has UV coordinates, applying texture mapping`);
            // Force texture to use the mesh's UV coordinates
            eyesTexture.needsUpdate = true;
          }
          
          console.log(`Set ${child.name} to screen material with eyes texture`);
        } else if (child.name === 'head_screen_low' || child.name.includes('head_screen')) {
          // Apply black material to the screen mesh
          child.material = blackMaterial;
          console.log(`Set ${child.name} to black material`);
        } else if (
          child.name === 'head_mohawk_low001' || 
          child.name.includes('mohawk') ||
          child.name === 'ears_low001' || 
          child.name.includes('ears') ||
          child.name === 'ear_joint_low001' || 
          child.name.includes('ear_joint') ||
          child.name === 'screw_low001' || 
          child.name.includes('screw') ||
          child.name === 'torso_mid_low001' ||
          child.name.includes('torso_mid')
        ) {
          // Set pink material
          child.material = pinkMaterial.clone();
          console.log(`Set ${child.name} to pink material`);
        } else {
          // Set white material
          child.material = whiteMaterial.clone();
          console.log(`Set ${child.name} to white material`);
        }
      }
    });
    
    // Create animation mixer for the current model
    const mixer = new THREE.AnimationMixer(gltf.scene);
    mixerRef.current = mixer;
    
    // Set up animations
    if (danceGltf.animations && danceGltf.animations.length > 0) {
      console.log('Found animations in dance.glb:', danceGltf.animations);
      // Ensure we're using global animations
      const danceClip = danceGltf.animations[0].clone();
      // Convert to global space if needed
      danceClip.tracks.forEach(track => {
        if (track.name.includes('.quaternion') || track.name.includes('.position')) {
          console.log(`Processing track: ${track.name}`);
        }
      });
      const danceAction = mixer.clipAction(danceClip);
      danceActionRef.current = danceAction;
      danceAction.play();
    } else {
      console.warn('No animations found in dance.glb');
    }
    
    if (fallGltf.animations && fallGltf.animations.length > 0) {
      console.log('Found animations in fall.glb:', fallGltf.animations);
      // Clone the animation to avoid conflicts
      const fallClip = fallGltf.animations[0].clone();
      // Ensure we're using global animations
      fallClip.tracks.forEach(track => {
        if (track.name.includes('.quaternion') || track.name.includes('.position')) {
          console.log(`Processing track: ${track.name}`);
        }
      });
      const fallAction = mixer.clipAction(fallClip);
      fallAction.loop = THREE.LoopOnce;
      fallAction.clampWhenFinished = true;
      fallActionRef.current = fallAction;
      
      // Pre-initialize the fall action but with zero weight
      fallAction.weight = 0;
      fallAction.play();
      fallAction.stop();
    } else {
      console.warn('No animations found in fall.glb');
    }
    
    if (standGltf.animations && standGltf.animations.length > 0) {
      console.log('Found animations in stand.glb:', standGltf.animations);
      // Clone the animation to avoid conflicts
      const standClip = standGltf.animations[0].clone();
      // Ensure we're using global animations
      standClip.tracks.forEach(track => {
        if (track.name.includes('.quaternion') || track.name.includes('.position')) {
          console.log(`Processing track: ${track.name}`);
        }
      });
      const standAction = mixer.clipAction(standClip);
      standAction.loop = THREE.LoopOnce;
      standAction.clampWhenFinished = true;
      standActionRef.current = standAction;
      
      // Pre-initialize the stand action but with zero weight
      standAction.weight = 0;
      standAction.play();
      standAction.stop();
    } else {
      console.warn('No animations found in stand.glb');
    }
    
    setIsInitialized(true);
    
    // Clean up
    return () => {
      if (mixer) {
        mixer.stopAllAction();
        mixer.uncacheRoot(gltf.scene);
      }
    };
  }, [gltf, danceGltf, fallGltf, standGltf, eyesTexture]);
  
  // Set up listener for when fall animation completes
  useEffect(() => {
    if (mixerRef.current) {
      const mixer = mixerRef.current;
      
      // Store the initial position, rotation, and scale of the model when it first loads
      if (gltf.scene && !initialStateRef.current) {
        initialStateRef.current = {
          position: gltf.scene.position.clone(),
          rotation: gltf.scene.rotation.clone(),
          scale: gltf.scene.scale.clone()
        };
        console.log('Stored initial model state:', initialStateRef.current);
      }
      
      mixer.addEventListener('finished', (e) => {
        console.log('Animation finished:', e.action);
        if (e.action === fallActionRef.current) {
          console.log('Fall animation completed, transitioning to stand');
          
          // Play the stand animation
          if (standActionRef.current) {
            console.log('Playing stand animation');
            standActionRef.current.reset();
            standActionRef.current.setEffectiveTimeScale(1);
            standActionRef.current.setEffectiveWeight(1);
            standActionRef.current.enabled = true;
            standActionRef.current.time = 0;
            standActionRef.current.paused = false;
            standActionRef.current.play();
          } else {
            console.error('Stand animation not available');
            resetModelAndPlayDance();
          }
        } else if (e.action === standActionRef.current) {
          console.log('Stand animation completed, resetting model and playing dance');
          resetModelAndPlayDance();
        }
      });
    }
  }, [gltf.scene, standActionRef, danceGltf.animations]);
  
  // New function to completely reset the model and play dance
  const resetModelAndPlayDance = () => {
    // First, stop all current animations
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
    }
    
    // Reset the model to its initial state
    if (gltf.scene && initialStateRef.current) {
      console.log('Resetting model to initial state:', initialStateRef.current);
      
      // Reset position, rotation, and scale to initial values
      gltf.scene.position.copy(initialStateRef.current.position);
      gltf.scene.rotation.copy(initialStateRef.current.rotation);
      gltf.scene.scale.copy(initialStateRef.current.scale);
      
      // Force update the matrix
      gltf.scene.updateMatrix();
      gltf.scene.updateMatrixWorld(true);
    } else {
      // Fallback to hardcoded values if initial state wasn't captured
      console.warn('Initial state not available, using default values');
      gltf.scene.position.set(0, -1, 0);
      gltf.scene.rotation.set(0, 0, 0);
      gltf.scene.scale.set(1.5, 1.5, 1.5);
      gltf.scene.updateMatrix();
      gltf.scene.updateMatrixWorld(true);
    }
    
    // Create a fresh dance animation
    if (danceGltf.animations && danceGltf.animations.length > 0) {
      // Clone the animation to ensure it's fresh
      const freshDanceClip = danceGltf.animations[0].clone();
      const newDanceAction = mixerRef.current.clipAction(freshDanceClip);
      
      // Store the new dance action
      danceActionRef.current = newDanceAction;
      
      // Configure the dance animation
      newDanceAction.reset();
      newDanceAction.setEffectiveTimeScale(1);
      newDanceAction.setEffectiveWeight(1);
      newDanceAction.play();
    }
    
    setIsPlaying(true);
  };
  
  // Function to play fall animation when model is clicked
  const playFallAnimation = () => {
    if (!isInitialized) return;
    
    console.log('Playing fall animation');
    
    // Stop current dance animation
    if (danceActionRef.current) {
      danceActionRef.current.fadeOut(0.3);
    }
    
    // Play fall animation
    if (fallActionRef.current) {
      console.log('Fall animation exists, playing it now');
      fallActionRef.current.reset();
      fallActionRef.current.setEffectiveTimeScale(1);
      fallActionRef.current.setEffectiveWeight(1);
      fallActionRef.current.enabled = true;
      fallActionRef.current.time = 0;
      fallActionRef.current.paused = false;
      fallActionRef.current.play();
      setIsPlaying(false);
    } else {
      console.error('Fall animation not available');
    }
  };
  
  // Animation frame update
  useFrame((state, delta) => {
    if (mixerRef.current) {
      mixerRef.current.update(delta);
    }
  });
  
  return (
    <>
      <primitive 
        object={gltf.scene} 
        scale={1.5} 
        position={[0, -1, 0]} 
        onClick={playFallAnimation}
        ref={modelRef}
      />
    </>
  );
};

// Main HeroSection component
const HeroSection = () => {
  // Initialize state with default values
  const [textureOffset, setTextureOffset] = useState({ x: 0, y: 0.66 });
  const [textureTiling, setTextureTiling] = useState({ x: 1, y: 1 });
  const [flipY, setFlipY] = useState(false);
  
  // Handle changes to texture offset and tiling
  const handleOffsetXChange = (e) => setTextureOffset(prev => ({ ...prev, x: parseFloat(e.target.value) }));
  const handleOffsetYChange = (e) => setTextureOffset(prev => ({ ...prev, y: parseFloat(e.target.value) }));
  const handleTilingXChange = (e) => setTextureTiling(prev => ({ ...prev, x: parseFloat(e.target.value) }));
  const handleTilingYChange = (e) => setTextureTiling(prev => ({ ...prev, y: parseFloat(e.target.value) }));
  const handleFlipYChange = () => setFlipY(prev => !prev);
  
  // Add this useEffect hook inside your component
  useEffect(() => {
    // Function to animate the bubbles
    const animateBubbles = () => {
      const smallBubble = document.getElementById('small-bubble');
      const mediumBubble = document.getElementById('medium-bubble');
      const mainBubble = document.getElementById('main-bubble');
      
      if (!smallBubble || !mediumBubble || !mainBubble) {
        console.error('Could not find bubble elements');
        return;
      }
      
      console.log('Animating bubbles');
      
      // Reset all bubbles to invisible
      smallBubble.style.opacity = '0';
      mediumBubble.style.opacity = '0';
      mainBubble.style.opacity = '0';
      
      // Animate small bubble after a delay - faster now
      setTimeout(() => {
        console.log('Showing small bubble');
        smallBubble.style.transition = 'opacity 0.5s ease-out';
        smallBubble.style.opacity = '1';
      }, 500); // Reduced from 1000ms to 500ms
      
      // Animate medium bubble after a delay - faster now
      setTimeout(() => {
        console.log('Showing medium bubble');
        mediumBubble.style.transition = 'opacity 0.5s ease-out';
        mediumBubble.style.opacity = '1';
      }, 1000); // Reduced from 3000ms to 1000ms
      
      // Animate main bubble after a delay - faster now
      setTimeout(() => {
        console.log('Showing main bubble');
        mainBubble.style.transition = 'opacity 0.5s ease-out';
        mainBubble.style.opacity = '1';
      }, 1500); // Reduced from 5000ms to 1500ms
    };
    
    // Run the animation once when component mounts
    setTimeout(animateBubbles, 300); // Reduced from 500ms to 300ms
    
  }, []); // Empty dependency array means this runs once on mount
  
  return (
    <section className="hero-section">
      <div className="hero-content">
        <h1>You Plan the Event, We Add the Tech</h1>
        <p>Innovative & Immersive</p>
        <div className="hero-buttons">
          <Link to="/about" className="primary-btn">Learn More</Link>
          <Link to="/contact" className="secondary-btn">Contact Us</Link>
        </div>
      </div>
      
     {/* <div className="texture-controls">
        <div className="control-group">
          <label>Offset X: {textureOffset.x.toFixed(2)}</label>
          <input 
            type="range" 
            min="-1" 
            max="1" 
            step="0.01" 
            value={textureOffset.x} 
            onChange={handleOffsetXChange} 
          />
        </div>
        <div className="control-group">
          <label>Offset Y: {textureOffset.y.toFixed(2)}</label>
          <input 
            type="range" 
            min="-1" 
            max="1" 
            step="0.01" 
            value={textureOffset.y} 
            onChange={handleOffsetYChange} 
          />
        </div>
        <div className="control-group">
          <label>Tiling X: {textureTiling.x.toFixed(2)}</label>
          <input 
            type="range" 
            min="0.1" 
            max="10" 
            step="0.1" 
            value={textureTiling.x} 
            onChange={handleTilingXChange} 
          />
        </div>
        <div className="control-group">
          <label>Tiling Y: {textureTiling.y.toFixed(2)}</label>
          <input 
            type="range" 
            min="0.1" 
            max="10" 
            step="0.1" 
            value={textureTiling.y} 
            onChange={handleTilingYChange} 
          />
        </div>
        <div className="control-group checkbox">
          <label>
            <input 
              type="checkbox" 
              checked={flipY} 
              onChange={handleFlipYChange} 
            />
            Flip Y Axis
          </label>
        </div>
      </div> */}
      
      <div className="hero-model">
        <Suspense fallback={<div>Loading 3D Model...</div>}>
          <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
            {/* Enhanced lighting setup for better visibility of white parts */}
            <ambientLight intensity={1.2} />
            <spotLight 
              position={[10, 10, 10]} 
              angle={0.3} 
              penumbra={1} 
              intensity={2}
              castShadow 
            />
            <pointLight position={[-10, -10, -10]} intensity={0.8} />
            <pointLight position={[0, 5, 5]} intensity={1.2} />
            <directionalLight position={[0, 5, 0]} intensity={0.8} color="#ffffff" />
            <Model 
              textureOffset={textureOffset} 
              textureTiling={textureTiling} 
              flipY={flipY}
            />
          </Canvas>
        </Suspense>
        
        {/* Thought bubble container */}
        <div className="thought-bubble-container">
          <div id="small-bubble" className="bubble small-bubble"></div>
          <div id="medium-bubble" className="bubble medium-bubble"></div>
          <div id="main-bubble" className="bubble main-bubble">
            <div className="thought-content">Do not touch me</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 